import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";
import { CalendarIcon, Check, Loader2, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const EMAILJS_SERVICE_ID = "service_c3rszhq";
const EMAILJS_TEMPLATE_ID = "template_u8zxx0x";
const EMAILJS_PUBLIC_KEY = "PsMvOGd7GcHP8NNXV";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

const OCCASION_TYPES = [
  { value: "private_dinner", label: "Privé diner" },
  { value: "wedding", label: "Bruiloft" },
  { value: "birthday", label: "Verjaardag" },
  { value: "business", label: "Zakelijk diner" },
  { value: "other", label: "Anders, namelijk..." },
];

const INDOOR_OUTDOOR_OPTIONS = [
  { value: "indoor", label: "Binnen" },
  { value: "outdoor", label: "Buiten" },
  { value: "both", label: "Beide" },
];

export default function QuotePage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [date, setDate] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    event_date: "",
    location: "",
    occasion_type: "",
    occasion_other: "",
    indoor_outdoor: "",
    guest_count: "",
    music: false,
    styling: false,
    tableware: false,
    flowers: false,
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      setFormData((prev) => ({
        ...prev,
        event_date: format(selectedDate, "yyyy-MM-dd"),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    if (!formData.event_date) {
      toast.error("Selecteer een datum voor je event");
      return;
    }

    if (!formData.occasion_type) {
      toast.error("Selecteer het type gelegenheid");
      return;
    }

    if (formData.occasion_type === "other" && !formData.occasion_other.trim()) {
      toast.error("Vul in wat voor gelegenheid het is");
      return;
    }

    if (!formData.guest_count || parseInt(formData.guest_count) < 1) {
      toast.error("Vul het aantal gasten in");
      return;
    }

    setIsSubmitting(true);

    try {
      const templateParams = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        event_date: formData.event_date,
        location: formData.location || "Niet opgegeven",
        occasion_type: formData.occasion_type === "other"
          ? `Anders: ${formData.occasion_other}`
          : OCCASION_TYPES.find((t) => t.value === formData.occasion_type)?.label || formData.occasion_type,
        indoor_outdoor: formData.indoor_outdoor || "Niet opgegeven",
        guest_count: formData.guest_count,
        extras: [
          formData.music && "Muziek",
          formData.styling && "Styling",
          formData.tableware && "Servies & glaswerk",
          formData.flowers && "Bloemen",
        ].filter(Boolean).join(", ") || "Geen",
        message: formData.message || "Geen bericht",
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setIsSuccess(true);
      toast.success("Je aanvraag is succesvol verzonden!");

    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Er is iets misgegaan. Probeer het opnieuw.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <main className="pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="w-20 h-20 rounded-full bg-blue/10 flex items-center justify-center mx-auto mb-8">
              <Check className="w-10 h-10 text-blue" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl text-burgundy mb-6 tracking-wide">
              Bedankt!
            </h1>
            <p className="text-burgundy/80 text-lg mb-4">
              We hebben je aanvraag ontvangen en nemen zo snel mogelijk contact met je op.
            </p>
            <p className="text-burgundy/60 mb-12">
              Een bevestiging is verzonden naar {formData.email}
            </p>
            <Link 
              to="/"
              data-testid="back-to-home-button"
              className="btn-secondary inline-block"
            >
              Terug naar home
            </Link>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      
      <main className="pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8 md:mb-12"
          >
            <Link 
              to="/"
              data-testid="back-link"
              className="inline-flex items-center text-burgundy/60 hover:text-burgundy transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-sm tracking-wide">Terug</span>
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            {...fadeInUp}
            className="text-center mb-16"
          >
            <h1 className="font-heading text-3xl md:text-5xl text-burgundy mb-4 tracking-wide">
              Offerte aanvragen
            </h1>
            <p className="text-burgundy/60 max-w-lg mx-auto">
              Vertel ons over je droomavond en wij maken er een onvergetelijke beleving van.
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-8 md:space-y-12 relative"
            data-testid="quote-form"
          >
            {/* Personal Info */}
            <div className="space-y-8">
              <h2 className="text-sm tracking-[0.2em] uppercase text-burgundy/60 pb-4 border-b border-burgundy/10">
                Persoonlijke gegevens
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder="Voornaam *"
                    className="form-input"
                    data-testid="input-first-name"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Achternaam *"
                    className="form-input"
                    data-testid="input-last-name"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="E-mailadres *"
                    className="form-input"
                    data-testid="input-email"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Telefoonnummer *"
                    className="form-input"
                    data-testid="input-phone"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="space-y-8">
              <h2 className="text-sm tracking-[0.2em] uppercase text-burgundy/60 pb-4 border-b border-burgundy/10">
                Event details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Date Picker */}
                <div>
                  <label className="block text-sm text-burgundy/60 mb-2">Datum event *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="date-picker-trigger"
                        className="form-input text-left flex items-center justify-between w-full"
                      >
                        {date ? (
                          <span>{format(date, "d MMMM yyyy", { locale: nl })}</span>
                        ) : (
                          <span className="text-burgundy/40">Selecteer een datum</span>
                        )}
                        <CalendarIcon className="w-4 h-4 text-burgundy/40" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        data-testid="calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm text-burgundy/60 mb-2">Locatie event</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Adres of NTB"
                    className="form-input"
                    data-testid="input-location"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Occasion Type */}
                <div className="relative z-20">
                  <label className="block text-sm text-burgundy/60 mb-2">Type gelegenheid *</label>
                  <Select
                    value={formData.occasion_type}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, occasion_type: value, occasion_other: "" }))
                    }
                  >
                    <SelectTrigger 
                      className="w-full bg-transparent border-0 border-b border-burgundy/20 rounded-none px-0 py-4 h-auto text-burgundy focus:ring-0 focus:border-blue"
                      data-testid="occasion-type-select"
                    >
                      <SelectValue placeholder="Selecteer type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-burgundy/10 z-50">
                      {OCCASION_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="cursor-pointer hover:bg-cream">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Other occasion input */}
                  {formData.occasion_type === "other" && (
                    <div className="mt-4">
                      <input
                        type="text"
                        name="occasion_other"
                        value={formData.occasion_other}
                        onChange={handleInputChange}
                        placeholder="Omschrijf de gelegenheid *"
                        className="form-input"
                        data-testid="input-occasion-other"
                        required
                      />
                    </div>
                  )}
                </div>

                {/* Indoor/Outdoor */}
                <div className="relative z-20">
                  <label className="block text-sm text-burgundy/60 mb-2">Binnen / Buiten</label>
                  <Select
                    value={formData.indoor_outdoor}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, indoor_outdoor: value }))
                    }
                  >
                    <SelectTrigger 
                      className="w-full bg-transparent border-0 border-b border-burgundy/20 rounded-none px-0 py-4 h-auto text-burgundy focus:ring-0 focus:border-blue"
                      data-testid="indoor-outdoor-select"
                    >
                      <SelectValue placeholder="Selecteer optie" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-burgundy/10 z-50">
                      {INDOOR_OUTDOOR_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="cursor-pointer hover:bg-cream">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Guest Count */}
              <div className="max-w-xs">
                <label className="block text-sm text-burgundy/60 mb-2">Aantal gasten *</label>
                <input
                  type="number"
                  name="guest_count"
                  value={formData.guest_count}
                  onChange={handleInputChange}
                  placeholder="Aantal"
                  min="1"
                  className="form-input"
                  data-testid="input-guest-count"
                  required
                />
              </div>
            </div>

            {/* Extra Services */}
            <div className="space-y-8">
              <h2 className="text-sm tracking-[0.2em] uppercase text-burgundy/60 pb-4 border-b border-burgundy/10">
                Extra services
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { name: "music", label: "Muziek" },
                  { name: "styling", label: "Styling" },
                  { name: "tableware", label: "Servies & glaswerk" },
                  { name: "flowers", label: "Bloemen" },
                ].map((service) => (
                  <div key={service.name} className="flex items-center gap-3">
                    <Checkbox
                      id={service.name}
                      checked={formData[service.name]}
                      onCheckedChange={(checked) =>
                        handleCheckboxChange(service.name, checked)
                      }
                      className="h-5 w-5 border-2 border-burgundy/30 data-[state=checked]:bg-burgundy data-[state=checked]:border-burgundy rounded-sm"
                      data-testid={`checkbox-${service.name}`}
                    />
                    <Label
                      htmlFor={service.name}
                      className="text-sm text-burgundy cursor-pointer select-none"
                    >
                      {service.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Message */}
            <div className="space-y-4">
              <h2 className="text-sm tracking-[0.2em] uppercase text-burgundy/60 pb-4 border-b border-burgundy/10">
                Jouw verhaal
              </h2>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Vertel iets over je aanvraag..."
                rows={6}
                className="form-input resize-none"
                data-testid="input-message"
              />
            </div>

            {/* Submit */}
            <div className="pt-8 relative z-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-burgundy text-white px-8 py-4 text-sm uppercase tracking-widest hover:bg-blue transition-colors duration-500 border border-burgundy hover:border-blue w-full md:w-auto flex items-center justify-center gap-2 cursor-pointer"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verzenden...
                  </>
                ) : (
                  "Verstuur aanvraag"
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </main>

      <Footer />
    </div>
  );
}
